import React from "react";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import { formatForDisplay } from "./utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const EVENT_TYPES = [
    { value: "addClient", label: "Add Client" },
    { value: "activateClient", label: "Activate Client" },
    { value: "rejectClient", label: "Reject Client" },
    { value: "birthday", label: "Birthday" },
    { value: "createLoan", label: "Create Loan" },
    { value: "approveLoan", label: "Approve Loan" },
    { value: "disburseLoan", label: "Disburse Loan" },
    { value: "rejectLoan", label: "Reject Loan" },
    { value: "overdueLoan", label: "Overdue Loan" },
    { value: "installmentAlert", label: "Installment Alert" },
    { value: "addPayment", label: "Add Payment" },
];

const INITIAL_FORM_VALUES = {
    event_type: "",
    phone_number: "",
    loan_account_no: "",
    musoni_client_id: "",
    min_date: null,
    max_date: null,
    cursor: "",
    limit: "25",
};

const getMessageId = (message, index) => {
    if (!message) {
        return `unknown-${index}`;
    }

    return (
        message.id ??
        message.message_id ??
        message.sms_id ??
        message.uuid ??
        `${message.to_number ?? message.phone_number ?? "unknown"}-${message.sent_at ?? message.created_at ?? index}`
    );
};

const formatDateTime = (value) => {
    if (!value) return "—";

    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString();
    } catch (error) {
        return value;
    }
};

function SmsMessages() {
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    const [messages, setMessages] = React.useState([]);
    const [formValues, setFormValues] = React.useState(INITIAL_FORM_VALUES);
    const [queryFilters, setQueryFilters] = React.useState(INITIAL_FORM_VALUES);
    const [cursorParam, setCursorParam] = React.useState(null);
    const [nextCursor, setNextCursor] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [hasMore, setHasMore] = React.useState(true);
    const [exporting, setExporting] = React.useState(false);

    const observer = React.useRef();

    React.useEffect(() => {
        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, []);

    const buildQueryParams = React.useCallback(() => {
        const params = new URLSearchParams();
        const {
            event_type,
            phone_number,
            loan_account_no,
            musoni_client_id,
            min_date,
            max_date,
            limit,
        } = queryFilters;

        const formatDateParam = (value) => {
            if (!(value instanceof Date)) {
                return null;
            }

            const timestamp = value.getTime();
            return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
        };

        if (event_type?.trim()) {
            params.append("event_type", event_type.trim());
        }

        if (phone_number?.trim()) {
            params.append("phone_number", phone_number.trim());
        }

        if (loan_account_no?.trim()) {
            params.append("loan_account_no", loan_account_no.trim());
        }

        if (musoni_client_id?.trim()) {
            params.append("musoni_client_id", musoni_client_id.trim());
        }

        const formattedMinDate = formatDateParam(min_date);
        if (formattedMinDate) {
            params.append("min_date", formattedMinDate);
        }

        const formattedMaxDate = formatDateParam(max_date);
        if (formattedMaxDate) {
            params.append("max_date", formattedMaxDate);
        }

        const numericLimit = Number(limit);
        if (Number.isFinite(numericLimit) && numericLimit > 0) {
            params.append("limit", String(numericLimit));
        }

        if (cursorParam) {
            params.append("cursor", cursorParam);
        }

        return params;
    }, [queryFilters, cursorParam]);

    const fetchMessages = React.useCallback(async () => {
        if (!auth?.accessToken) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = buildQueryParams();
            const queryString = params.toString();
            const url = queryString ? `/sms/messages?${queryString}` : "/sms/messages";

            const response = await axiosPrivate.get(url, {
                headers: { Authorization: "Bearer " + auth.accessToken },
            });

            const payload = response?.data ?? {};
            const items = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.messages)
                    ? payload.messages
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];

            const derivedNextCursor = Array.isArray(payload)
                ? null
                : payload?.next_cursor ??
                    payload?.nextCursor ??
                    payload?.cursor ??
                    payload?.meta?.next_cursor ??
                    null;

            setMessages(prevMessages => (
                cursorParam ? [...prevMessages, ...items] : items
            ));
            setNextCursor(derivedNextCursor);
            const moreFromCursor = Boolean(derivedNextCursor);
            const moreFromFlag = Boolean(
                Array.isArray(payload)
                    ? false
                    : payload?.has_more ??
                        payload?.hasMore ??
                        payload?.meta?.has_more
            );
            setHasMore(moreFromCursor || moreFromFlag);

            if (!moreFromCursor && !moreFromFlag && items.length === 0) {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Error fetching SMS messages:", err);
            setError("Failed to load SMS messages.");
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [auth?.accessToken, axiosPrivate, buildQueryParams, cursorParam]);

    React.useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }

        fetchMessages();
    }, [auth?.accessToken, fetchMessages]);

    const lastRowRef = React.useCallback(node => {
        if (loading) return;
        if (!hasMore) return;
        if (!nextCursor) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                setCursorParam(prevCursor => (prevCursor === nextCursor ? prevCursor : nextCursor));
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore, nextCursor]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormValues(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDateChange = (name, dateValue) => {
        setFormValues(prev => ({
            ...prev,
            [name]: dateValue instanceof Date && !Number.isNaN(dateValue.getTime()) ? dateValue : null,
        }));
    };

    const handleApplyFilters = (event) => {
        event.preventDefault();

        setCursorParam(formValues.cursor?.trim() ? formValues.cursor.trim() : null);
        setNextCursor(null);
        setMessages([]);
        setHasMore(true);
        setError(null);
        setQueryFilters({
            event_type: formValues.event_type,
            phone_number: formValues.phone_number,
            loan_account_no: formValues.loan_account_no,
            musoni_client_id: formValues.musoni_client_id,
            min_date: formValues.min_date,
            max_date: formValues.max_date,
            cursor: formValues.cursor,
            limit: formValues.limit,
        });
    };

    const handleResetFilters = () => {
        setFormValues(INITIAL_FORM_VALUES);
        setQueryFilters(INITIAL_FORM_VALUES);
        setCursorParam(null);
        setNextCursor(null);
        setMessages([]);
        setHasMore(true);
        setError(null);
    };

    const handleExportMessages = async () => {
        if (!auth?.accessToken || exporting) {
            return;
        }

        setExporting(true);

        try {
            const params = new URLSearchParams();
            const {
                event_type,
                phone_number,
                loan_account_no,
                musoni_client_id,
                min_date,
                max_date,
                limit,
            } = queryFilters;

            const formatDateParam = (value) => {
                if (!(value instanceof Date)) {
                    return null;
                }

                const timestamp = value.getTime();
                return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
            };

            if (event_type?.trim()) {
                params.append("event_type", event_type.trim());
            }

            if (phone_number?.trim()) {
                params.append("phone_number", phone_number.trim());
            }

            if (loan_account_no?.trim()) {
                params.append("loan_account_no", loan_account_no.trim());
            }

            if (musoni_client_id?.trim()) {
                params.append("musoni_client_id", musoni_client_id.trim());
            }

            const formattedMinDate = formatDateParam(min_date);
            if (formattedMinDate) {
                params.append("min_date", formattedMinDate);
            }

            const formattedMaxDate = formatDateParam(max_date);
            if (formattedMaxDate) {
                params.append("max_date", formattedMaxDate);
            }

            const numericLimit = Number(limit);
            if (Number.isFinite(numericLimit) && numericLimit > 0) {
                params.append("limit", String(numericLimit));
            }

            const queryString = params.toString();
            const url = queryString ? `/sms/messages/export?${queryString}` : "/sms/messages/export";

            const response = await axiosPrivate.get(url, {
                headers: { Authorization: "Bearer " + auth.accessToken },
                responseType: "blob",
            });

            const href = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", "messages.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (err) {
            console.error("Error exporting SMS messages:", err);
            setError("Failed to export SMS messages.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-slate-100 p-6">
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">SMS Messages</h2>
                        <p className="text-sm text-gray-500">Review messages sent to clients.</p>
                    </div>
                </div>

                <form onSubmit={handleApplyFilters} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="event_type">
                                Event Type
                            </label>
                            <select
                                id="event_type"
                                name="event_type"
                                value={formValues.event_type}
                                onChange={handleInputChange}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">All Event Types</option>
                                {EVENT_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="phone_number">
                                Phone Number
                            </label>
                            <input
                                id="phone_number"
                                name="phone_number"
                                value={formValues.phone_number}
                                onChange={handleInputChange}
                                placeholder="e.g. 2547..."
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="loan_account_no">
                                Loan Account No
                            </label>
                            <input
                                id="loan_account_no"
                                name="loan_account_no"
                                value={formValues.loan_account_no}
                                onChange={handleInputChange}
                                placeholder="Account number"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="musoni_client_id">
                                Musoni Client ID
                            </label>
                            <input
                                id="musoni_client_id"
                                name="musoni_client_id"
                                value={formValues.musoni_client_id}
                                onChange={handleInputChange}
                                placeholder="Client identifier"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="min_date">
                                Min Date/Time
                            </label>
                            <DatePicker
                                id="min_date"
                                selected={formValues.min_date}
                                onChange={date => handleDateChange("min_date", date)}
                                selectsStart
                                startDate={formValues.min_date}
                                endDate={formValues.max_date}
                                showTimeSelect
                                timeIntervals={15}
                                isClearable
                                placeholderText="Select minimum date/time"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                wrapperClassName="w-full"
                                dateFormat="yyyy-MM-dd HH:mm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="max_date">
                                Max Date/Time
                            </label>
                            <DatePicker
                                id="max_date"
                                selected={formValues.max_date}
                                onChange={date => handleDateChange("max_date", date)}
                                selectsEnd
                                startDate={formValues.min_date}
                                endDate={formValues.max_date}
                                minDate={formValues.min_date}
                                showTimeSelect
                                timeIntervals={15}
                                isClearable
                                placeholderText="Select maximum date/time"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                wrapperClassName="w-full"
                                dateFormat="yyyy-MM-dd HH:mm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" htmlFor="limit">
                                Limit
                            </label>
                            <select
                                id="limit"
                                name="limit"
                                value={formValues.limit}
                                onChange={handleInputChange}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleResetFilters}
                            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Clear Filters
                        </button>
                        <button
                            type="submit"
                            className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                            Apply Filters
                        </button>
                        <button
                            type="button"
                            onClick={handleExportMessages}
                            disabled={exporting}
                            className={`rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${exporting ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                            {exporting ? "Exporting..." : "Download Excel"}
                        </button>
                    </div>
                </form>
            </div>

            {error && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-medium text-gray-800">Messages</h3>
                        <p className="text-sm text-gray-500">
                            {messages.length} message{messages.length === 1 ? "" : "s"}
                        </p>
                    </div>
                </div>

                {messages.length > 0 ? (
                    <div className="overflow-hidden">
                        <div className="max-h-96 overflow-y-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loan Account No</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Musoni Client ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period Start</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {messages.map((message, index) => {
                                        const rowContent = (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{message?.event_type ?? "—"}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatForDisplay(message?.to_number ?? message?.phone_number)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{message?.display_name ?? "—"}</td>
                                                <td className="px-6 py-4 text-sm text-gray-700">
                                                    <div className="max-w-md whitespace-pre-wrap break-words">
                                                        {message?.body_rendered ?? message?.body ?? message?.message ?? "—"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{message?.loan_account_no ?? "—"}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{message?.musoni_client_id ?? message?.client_id ?? "—"}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{message?.status ?? message?.delivery_status ?? "—"}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDateTime(message?.sent_at ?? message?.created_at)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{message?.period_start ?? "—"}</td>
                                            </>
                                        );

                                        if (messages.length === index + 1) {
                                            return (
                                                <tr key={getMessageId(message, index)} ref={lastRowRef} className="hover:bg-gray-50">
                                                    {rowContent}
                                                </tr>
                                            );
                                        }

                                        return (
                                            <tr key={getMessageId(message, index)} className="hover:bg-gray-50">
                                                {rowContent}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {loading && (
                                <div className="px-6 py-4 text-sm text-gray-500">Loading…</div>
                            )}
                            {!hasMore && !loading && (
                                <div className="px-6 py-4 text-sm text-gray-500">No more messages to load.</div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        {loading ? "Loading messages…" : "No messages found"}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SmsMessages;
